// lib/growth/signup-attribution.ts
// GROWTH ENGINE — first-touch SIGNUP attribution (distinct from lead
// attribution in lib/attribution/*, which is per-lead). This closes the
// Loop 1 pipeline: a powered-by click lands on `/` carrying UTM; we stamp
// the first-touch UTM into a cookie and persist it on the NEW org at
// creation, so signups are attributable to the surface that produced them.
// Spec: contracts/product/POWERED_BY_MINDHUNTERS_SPEC_v1.md §4.

export const SIGNUP_ATTRIBUTION_COOKIE = 'mh_signup_attr';
const MAX_AGE_DAYS = 30;

export interface SignupAttribution {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  landed_at: string;
}

/** Parse UTM from a query string into the attribution shape (null if none). */
export function parseSignupAttribution(search: string, nowIso: string): SignupAttribution | null {
  const p = new URLSearchParams(search);
  const source = p.get('utm_source');
  if (!source) return null; // only capture attributed landings
  return {
    source,
    medium: p.get('utm_medium'),
    campaign: p.get('utm_campaign'),
    landed_at: nowIso,
  };
}

/**
 * Client: capture first-touch UTM into a cookie. FIRST-touch = never
 * overwrite an existing cookie (the surface that first brought them wins).
 * No-op when there's no UTM or a cookie already exists.
 */
export function captureSignupAttributionClient(): void {
  // Fully self-contained: the signup path is sacred. Attribution capture must
  // never throw into the marketing-site render tree (cookies disabled,
  // sandboxed iframe, exotic browsers) — a failure here is a no-op, never an
  // error the user can see.
  try {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (document.cookie.includes(`${SIGNUP_ATTRIBUTION_COOKIE}=`)) return;
    const attr = parseSignupAttribution(window.location.search, new Date().toISOString());
    if (!attr) return;
    const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie = `${SIGNUP_ATTRIBUTION_COOKIE}=${encodeURIComponent(
      JSON.stringify(attr),
    )}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    /* attribution is best-effort; never block or break the page */
  }
}

/** Server: safely parse the cookie value written by the client capture. */
export function readSignupAttributionCookie(raw: string | undefined | null): SignupAttribution | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<SignupAttribution>;
    if (!parsed || typeof parsed.source !== 'string') return null;
    return {
      source: parsed.source,
      medium: typeof parsed.medium === 'string' ? parsed.medium : null,
      campaign: typeof parsed.campaign === 'string' ? parsed.campaign : null,
      landed_at: typeof parsed.landed_at === 'string' ? parsed.landed_at : '',
    };
  } catch {
    return null;
  }
}
